/* eslint @typescript-eslint/strict-boolean-expressions: off */

import { gql } from "@apollo/client";
import { logger } from "lib/logger/browser";

import {
  useFindPublicProfileQuery,
  FindPublicProfileQuery,
} from "lib/types/graphql";

// For clarity, specify where value may be epmty string, even though
// it's not necessary to make TS happy.
interface PublicProfileType {
  id: number;
  name: string;
  is_available_for_gigs: boolean;

  profile_image_url: string | "";
  bio: string | "";
  discord_username: string | "";
  telegram_handle: string | "";
  twitter_handle: string | "";
  ethereum_address?: string;

  service_tier_name: string | "";
  received_stars: number;
  firstQueryCreatedDate: string | "";
  submittedContractsCount: number;
}

export default function useFetchPublicProfile(
  id: number
): PublicProfileType | undefined {
  const response = useFindPublicProfileQuery({
    variables: { id },
    onError: (error) => {
      logger.error(error);
    },
  });

  const user = response.data?.users_by_pk
    ? toPublicProfileType(response.data.users_by_pk)
    : undefined;

  return user;
}

// Not assigned to constant here. Using generated. See _GrahphQL requests_ in readme.
gql`
  query FindPublicProfile($id: Int!) {
    users_by_pk(id: $id) {
      id
      name
      profile_image_url
      bio
      is_available_for_gigs
      discord_username
      twitter_handle
      telegram_handle
      received_stars {
        sum
      }
      queries(order_by: { created_at: asc_nulls_last }, limit: 1) {
        created_at
      }
      conditional_public_attributes {
        ethereum_address
        service_tier_name
      }
      contract_submissions_aggregate(where: { status: { _eq: "processed" } }) {
        aggregate {
          count(distinct: true, columns: [address, blockchain])
        }
      }
    }
  }
`;

function toPublicProfileType(
  userResponse: FindPublicProfileQuery["users_by_pk"]
): PublicProfileType | undefined {
  if (!userResponse) {
    return undefined;
  }

  // Use empty string as default instead of null.
  // (`undefined` cannot be used because it's not JSON serializable.)
  const {
    id,
    name,
    is_available_for_gigs,

    profile_image_url = "",
    bio = "",
    discord_username = "",
    telegram_handle = "",
    twitter_handle = "",
  } = omitNulls(userResponse);

  return {
    id,
    name,
    is_available_for_gigs,

    profile_image_url,
    bio,
    discord_username,
    telegram_handle,
    twitter_handle,

    service_tier_name:
      userResponse.conditional_public_attributes?.service_tier_name ?? "",
    ethereum_address:
      userResponse.conditional_public_attributes?.ethereum_address || undefined,
    received_stars: userResponse.received_stars?.sum ?? 0,
    firstQueryCreatedDate: userResponse.queries[0]?.created_at ?? "",
    submittedContractsCount:
      userResponse.contract_submissions_aggregate.aggregate?.count ?? 0,
  };
}

function omitNulls(object: Record<string, any>) {
  return Object.fromEntries(
    Object.entries(object).filter(([, value]) => value !== null)
  );
}
